import {
  Droplets, FlaskConical, TestTube2, Microscope, MapPin, Phone, Mail,
  AlertTriangle, Wind, ThermometerSun, Bug, Droplet, Beaker,
  Wrench, Factory, ShieldCheck, ArrowRight, CheckCircle2,
} from "lucide-react";

export const CONTACT = {
  phone: "(07) 5327 3473",
  phoneLink: "tel:+61753273473",
  email: "lab@awarewatertesting.com.au",
  address: "1/37 Gateway Drive, Noosaville QLD 4566",
  hours: "Monday to Friday, 8:00am–4:00pm",
  sampleDropoff: "Drop-off before 2pm Monday to Thursday is best for same-week processing.",
};

export const TRUST_BADGES = [
  "Australian Water Testing Laboratory",
  "Easy-To-Read Reports",
  "Expert Recommendations Available",
  "Fast Turnaround",
  "Recognised Lab Methodologies",
  "Trusted By Homeowners & Trades",
];

// ============ PRICING — Single source of truth in /app/backend/core/constants.py ============
// `/api/pricing` is the canonical endpoint. These constants mirror it for
// initial render / offline fallback. They MUST stay aligned with the backend.
// Universal online prepaid discount: 20% OFF retail.
export const ONLINE_PREPAID_DISCOUNT_PCT = 20;

export const TESTS = [
  {
    id: "basic",
    name: "AWARE 4-Point",
    // Feb 2026 — All-Inclusive Pricing tier. Sticker price includes
    // postage and matches the server-side `all_inclusive_pricing`
    // config so the display price is exactly what Stripe charges.
    // The historical online-prepaid discount is folded into the
    // single all-inclusive tier — no separate discount is applied.
    retail: 129,
    online_prepaid: 129,
    member: 129,
    online: 129,
    regular: 129,
    save: 0,
    online_savings: 0,
    accent: "from-sky-500/10 to-cyan-500/10",
    ring: "ring-slate-200",
    icon: Droplets,
    tagline: "Is my private water supply safe to drink?",
    short: "A simple screening test for tank or bore water — determines whether your private supply shows signs of bacterial contamination.",
    bestFor: [
      "Homeowners wanting a quick answer on tank or bore water",
      "Entry-level safety screening for private water supplies",
      "Peace-of-mind on rainwater tank or bore drinking water",
    ],
    bestForShort: "Quick safety screening",
    category: "Entry-level test",
    // Feb 2026 — hero card highlights list. The 4-Point package covers
    // exactly four parameters so we list them individually.
    highlights: [
      "E. coli (Positive / Negative)",
      "Coliforms (Positive / Negative)",
      "pH",
      "Conductivity",
      "Plain-English Summary & Recommendations",
    ],
  },
  {
    id: "tank",
    name: "AWARE Tank 360",
    // Feb 2026 — All-Inclusive Pricing tier. Matches server-side
    // `all_inclusive_pricing` config; no separate online discount.
    retail: 209,
    online_prepaid: 209,
    member: 209,
    online: 209,
    regular: 209,
    save: 0,
    online_savings: 0,
    accent: "from-cyan-500/15 to-sky-600/15",
    ring: "ring-cyan-500",
    featured: true,
    icon: TestTube2,
    tagline: "The most popular test for rainwater tank owners",
    short: "Our most popular rainwater tank test, providing a broader understanding of your water quality and overall system health.",
    bestFor: [
      "Rainwater tank owners",
      "Professional household water assessment",
      "Whole-of-system tank check-up",
    ],
    bestForShort: "Most tank water households",
    category: "Recommended package",
    // Feb 2026 — the 360 packages cover 18–20 individual parameters, so
    // the hero card lists CATEGORIES rather than every parameter. The
    // dedicated per-package parameter grid further down the page still
    // enumerates the complete list.
    highlights: [
      "Microbiological (E. coli & Coliforms)",
      "Chemical Parameters (pH, TDS, Conductivity, Turbidity, Temperature, Alkalinity, Hardness)",
      "Soluble Cations (Calcium & Magnesium)",
      "Soluble Anions (Chloride, Nitrate, Ammonia, Phosphate, Sulphate)",
      "Metals (Iron & Copper)",
      "Plain-English Summary & Recommendations",
    ],
  },
  {
    id: "bore",
    name: "AWARE Bore 360",
    // Feb 2026 — All-Inclusive Pricing tier. Matches server-side
    // `all_inclusive_pricing` config; no separate online discount.
    retail: 229,
    online_prepaid: 229,
    member: 229,
    online: 229,
    regular: 229,
    save: 0,
    online_savings: 0,
    accent: "from-sky-600/10 to-slate-900/10",
    ring: "ring-slate-200",
    icon: FlaskConical,
    tagline: "Understand what's really in your bore water",
    short: "Designed specifically for bore water users to identify common water quality concerns and treatment requirements.",
    bestFor: [
      "Property owners on bore water",
      "Drinking, domestic, irrigation or livestock use",
      "Additional water quality indicators relevant to bore water",
    ],
    bestForShort: "Bore water properties",
    category: "Bore water testing",
    // Feb 2026 — bore mirrors tank at the category level and adds
    // Nitrite (Anions) and Manganese (Metals) which are more relevant
    // to groundwater chemistry.
    highlights: [
      "Microbiological (E. coli & Coliforms)",
      "Chemical Parameters (pH, TDS, Conductivity, Turbidity, Temperature, Alkalinity, Hardness)",
      "Soluble Cations (Calcium & Magnesium)",
      "Soluble Anions incl. Nitrite (Chloride, Nitrate, Nitrite, Ammonia, Phosphate, Sulphate)",
      "Metals incl. Manganese (Manganese, Iron & Copper)",
      "Plain-English Summary & Recommendations",
    ],
  },
];

// AWARE Water Health Record — Annual Membership constants for frontend
export const MEMBERSHIP = {
  id: "passport_annual",
  name: "AWARE Water Health Record",
  price: 99,
  currency: "AUD",
  interval: "year",
  benefits: [
    "AWARE Water Health Record",
    "Historical Water Test Archive",
    "Water Health Score™",
    "Trend Graphs",
    "AI Summary Reports",
    "Retest Reminders",
    "Property Water History",
    "Priority Support",
    "Member Pricing On Future Testing",
  ],
};

export const COMPARISON = {
  others: [
    "Technical reports only",
    "Difficult to understand",
    "Generic results",
    "No practical guidance",
    "No solution pathway",
  ],
  us: [
    "Professional laboratory analysis",
    "Easy-to-read summary report",
    "Plain-English explanations",
    "Practical recommendations",
    "Free Pristine Water Systems proposal where corrective work is suggested",
    "Real-world treatment expertise",
  ],
};

export const STEPS = [
  { n: "01", title: "Book Online", desc: "Choose your test and prepay in under 90 seconds." },
  { n: "02", title: "Collect Sample", desc: "Follow our simple guide — labels and instructions included." },
  { n: "03", title: "Deliver To Lab", desc: "Drop in or post to our Noosaville laboratory." },
  { n: "04", title: "Laboratory Testing", desc: "Recognised laboratory methodologies and documented quality-assurance procedures." },
  { n: "05", title: "Results & Summary", desc: "Certificate of Analysis + plain-English summary." },
  { n: "06", title: "Recommendations", desc: "Practical next steps included in every report — no upsell required." },
];

export const PROBLEMS = [
  { slug: "cloudy", title: "Cloudy Water", icon: Droplets, hint: "Turbidity & sediment check" },
  { slug: "bad-taste", title: "Bad Taste", icon: TestTube2, hint: "Mineral & chlorine analysis" },
  { slug: "bad-smell", title: "Bad Smell", icon: Wind, hint: "Sulphide & bacteria screen" },
  { slug: "ecoli", title: "E. coli Concerns", icon: Bug, hint: "Microbiological suite" },
  { slug: "coliform", title: "Coliform Bacteria", icon: Microscope, hint: "Full bacterial panel" },
  { slug: "low-ph", title: "Low pH / Acidic", icon: Beaker, hint: "pH, alkalinity, aggressiveness" },
  { slug: "iron", title: "Iron Staining", icon: Droplet, hint: "Iron, manganese & solids" },
  { slug: "manganese", title: "Manganese", icon: AlertTriangle, hint: "Heavy-metal analysis" },
  { slug: "filters", title: "Blocked Filters", icon: Wrench, hint: "Hardness, iron, sediment" },
  { slug: "tank", title: "Tank Water Problems", icon: ThermometerSun, hint: "Full suite tank panel" },
  { slug: "bore", title: "Bore Water Problems", icon: Factory, hint: "Comprehensive bore profile" },
  { slug: "safe", title: "Is It Safe To Drink?", icon: ShieldCheck, hint: "Drinking-water assessment" },
];

export const TESTIMONIALS = [
  {
    name: "Sarah K.",
    location: "Noosaville",
    text: "The report was incredibly clear — finally understood what was wrong with our tank water. The recommendations saved us from buying a filter we didn't need.",
    rating: 5,
  },
  {
    name: "Mark T.",
    location: "Buderim",
    text: "Fast turnaround and the summary actually explained things in plain English. Highly recommend for anyone on bore water.",
    rating: 5,
  },
  {
    name: "Pump Pro QLD",
    location: "Trade Partner",
    text: "We send all our client water samples here. Reliable, professional, and the white-label reports make our service look premium.",
    rating: 5,
  },
  {
    name: "Linda H.",
    location: "Cooroy",
    text: "After years of bad-tasting water we finally have answers. The plain-English summary made the next steps obvious.",
    rating: 5,
  },
];

export const SERVICE_AREAS = [
  "Noosa", "Noosaville", "Tewantin", "Cooroy", "Pomona", "Eumundi",
  "Doonan", "Yandina", "Coolum", "Buderim", "Maroochydore",
  "Nambour", "Maleny", "Montville", "Caloundra", "Mooloolaba",
];

export const ICONS = { ArrowRight, CheckCircle2, MapPin, Phone, Mail };
